import * as d3 from 'd3';
import { useCurrentUser } from '../../contexts/CurrentUser';
import { ampli } from '../../ampli';
import { format } from 'date-fns';
import { useEffect, useState } from "react";
import { Chart } from "react-google-charts";
import { API, ShareAPI } from "../../API";
import { useTranslation } from "react-i18next";
import { showRequestError } from '../../utils/requestError';
import styles from '../GeoChart/GeoChart.module.scss';
import { LoaderSmall } from "../LoaderSmall/LoaderSmall";
import { AddToNarrativeModal } from '../AddToNarrativeModal/AddToNarrativeModal';
import { AddToSourceGroupModal } from '../AddToSourceGroupModal/AddToSourceGroupModal';
import { WidgetContentModal } from '../WidgetContentModal/WidgetContentModal';

const geoChartData = {
  objects: [
    { name: 'Argentina', messages_count: 3, attitude: 25 },
    { name: 'Australia', messages_count: 53, attitude: 25 },
    { name: 'Belgium', messages_count: 3, attitude: 25 },
    { name: 'Brazil', messages_count: 138, attitude: 25 },
    { name: 'Canada', messages_count: 2, attitude: 25 },
    { name: 'China', messages_count: 705, attitude: 100 },
    { name: 'Egypt', messages_count: 7, attitude: 75 },
    { name: 'EU', messages_count: 55, attitude: 75 },
    { name: 'France', messages_count: 15, attitude: 25 },
    { name: 'Germany', messages_count: 12, attitude: 75 },
    { name: 'India', messages_count: 8, attitude: 25 },
    { name: 'Indonesia', messages_count: 43, attitude: 25 },
    { name: 'Iran', messages_count: 104, attitude: 75 },
    { name: 'Italy', messages_count: 10, attitude: 25 },
    { name: 'Japan', messages_count: 0, attitude: 25 },
    { name: 'Kazakhstan', messages_count: 61, attitude: 25 },
    { name: 'Libya', messages_count: 13, attitude: 75 },
    { name: 'Malaysia', messages_count: 8, attitude: 50 },
    { name: 'Mexico', messages_count: 0, attitude: 50 },
    { name: 'Netherlands', messages_count: 0, attitude: 75 },
    { name: 'Poland', messages_count: 7, attitude: 25 },
    { name: 'Russia', messages_count: 601, attitude: 75 },
    { name: 'Saudi Arabia', messages_count: 0, attitude: 50 },
    { name: 'South Africa', messages_count: 12, attitude: 25 },
    { name: 'South Korea', messages_count: 0, attitude: 75 },
    { name: 'Spain', messages_count: 4, attitude: 25 },
    { name: 'Switzerland', messages_count: 0, attitude: 75 },
    { name: 'Turkey', messages_count: 19, attitude: 50 },
    { name: 'UK', messages_count: 6, attitude: 75 },
  ],
};


export const AttitudeGeoChart = ({
  report,
  story,
  sourceGroups,
  countries,
  startDate,
  endDate,
  isShare,
}) => {
  const { t } = useTranslation();
  const { currentUser } = useCurrentUser();
  const [modal, setModal] = useState({ isVisible: false, country: null });
  const [countriesData, setCountriesData] = useState(null);
  const [allCountries, setAllCountries] = useState(null);
  const [addToCaseModal, setAddToCaseModal] = useState({ isOpen: false, countries: null });
  const [addToGroupModal, setAddToGroupModal] = useState({ isOpen: false, countries: null });

  const fetchAllCountries = () => {
    API.fetch('GET', '/API/v1/countries').then(data => {
      setAllCountries(data);
    }).catch((error) => showRequestError(error, t('Countries')));
  };

  useEffect(() => {
    fetchAllCountries();
  }, []);

  // const fetchCountriesData = () => {
  //   const urlParams = new URLSearchParams();
  //   if (sourceGroups.length > 0) {
  //     sourceGroups.forEach((sourceGroup) => {
  //       urlParams.append('source_group_ids', sourceGroup.value);
  //     });
  //   }
  //   if (countries.length > 0) {
  //     countries.forEach((country) => {
  //       urlParams.append('source_country_ids', country.value);
  //     });
  //   }
  //   if (startDate) {
  //     urlParams.append('date_from', format(startDate, 'yyyy-LL-dd'));
  //   }
  //   if (endDate) {
  //     urlParams.append('date_to', format(endDate, 'yyyy-LL-dd'));
  //   }

  //   API.fetch('GET', `/API/v1/topic-modeling/${report.id}/events/${story.id}/countries/breakdown?${urlParams.toString()}`).then(data => {
  //     setCountriesData(data);
  //   });
  // };

  // useEffect(() => {
  //   fetchCountriesData();
  // }, []);

  const getChartData = (data) => {
    if (!data?.objects?.length) return [['Country', 'Attitude', 'Messages'], ['No data', 0, 0]];
    let chartData = [[t('Country'), t('Attitude'), t('Publications')]];
    data?.objects?.forEach(country => {
      chartData.push([country.name, country.attitude, country.messages_count]);
    });
    return chartData;
  };

  const getCountryOption = (countryName) => {
    if (!allCountries) return null;
    let country = allCountries?.objects?.find(country => country.name === countryName);
    return country ? { value: country.id, label: country.name } : null;
  };

  const options = {
    region: "world",
    displayMode: "markers",
    colorAxis: { 
      colors: ["#B2DDFF", "#FFCC80", "#FFFBD1", "#ffb3b3",],
      values: [ 25, 50, 75, 100],
    },
    keepAspectRatio: true,
    magnifyingGlass: { enable: true, zoomFactor: 7.5 },
    datalessRegionColor: '#e9effd',
    legend: 'none',
    markerOpacity: 0.7,
  };

  if (!geoChartData) {
    return <div className={styles.widgetLoader}> <LoaderSmall /> </div>;
  }

  const mapStoriesId = {
    "China": 114035,
    "Russia": 114040,
  }

  return (
    <div className={styles.chartWrapper}>
      <Chart
        chartEvents={[
          {
            eventName: 'select',
            callback: ({ chartWrapper }) => {
              // getChart() is null when a click lands mid-redraw (OSAVUL-FRONTEND-G9)
              const chart = chartWrapper?.getChart();
              const dataTable = chartWrapper?.getDataTable();
              if (!chart || !dataTable) return;
              const selection = chart.getSelection();
              if (selection.length > 0) {
                var row = selection[0].row;
                let country = dataTable.getValue(row, 0); // Get country name from the selected row
                setModal({
                  isVisible: true,
                  country: country,
                });
                ampli.track({
                  event_type: 'Open widget popup in event',
                  event_properties: {
                    user_id: currentUser?.id,
                    workspace_id: currentUser?.workspace_id,
                    report_id: report?.id,
                    popup_type: 'Attitude GeoChart',
                    popup_value: 'Country: ' + country,
                  }
                });
              }
            },
          },
        ]}
        options={options}
        chartType="GeoChart"
        width="100%"
        height="500px"
        data={getChartData(geoChartData)}
        // mapsApiKey="AIzaSyD_zlgApSQgpEZHNntjwmQC1lcwU9c9fms"
        mapsApiKey='AIzaSyDSowaH_6CB0vrG63JvD13RJcBT--LZV-g'
        chartVersion="51"
      />

      <div className={styles.chartLegend}>
        <div className={styles.legendItem}>
          <div className={styles.legendColor} style={{ backgroundColor: '#B2DDFF', border: '1px solid #00368d', borderRadius: '50%' }}></div>
          <div className={styles.legendLabel}>{t('Negotiate')}</div>
        </div>
        <div className={styles.legendItem}>
          <div className={styles.legendColor} style={{ backgroundColor: '#FFCC80', border: '1px solid #D96C00', borderRadius: '50%' }}></div>
          <div className={styles.legendLabel}>{t('Pivot')}</div>
        </div>
        <div className={styles.legendItem}>
          <div className={styles.legendColor} style={{ backgroundColor: '#FFFBD1', border: '1px solid #A28F00', borderRadius: '50%' }}></div>
          <div className={styles.legendLabel}>{t('Counteract')}</div>
        </div>
        <div className={styles.legendItem}>
          <div className={styles.legendColor} style={{ backgroundColor: '#ffb3b3', border: '1px solid #6d0000', borderRadius: '50%' }}></div>
          <div className={styles.legendLabel}>{t('Attack')}</div>
        </div>
      </div>

      {!geoChartData.objects?.length ? (
        <div className={styles.noDataContainer}>
          {t('There is no data to display')}
        </div>
      ) : (
        ''
      )}

      {modal.isVisible ? (
        <WidgetContentModal
          infoModal={modal.isVisible}
          tabIndex={1}
          isChart={true}
          platformOption={null}
          type={null}
          subType={null}
          stateAffiliatedId={null}
          countryId={modal.country ? [getCountryOption(modal.country)] : null}
          audienceRange={null}
          narrative={report}
          sourceGroup={
            sourceGroups?.length > 0
              ? sourceGroups?.map((sourceGroup) => sourceGroup?.value)
              : null
          }
          source={null}
          sentimentOptions={null}
          eventIdsOptions={[mapStoriesId[modal.country]]}
          startDate={startDate}
          endDate={endDate}
          contentTypeOptions={null}
          isShare={isShare}
          onChange={(bool) => {
            setModal({ isVisible: bool, country: null });
          }}
          showAddToActions={true}
          onAddToCase={(bool) => {
            setAddToCaseModal({
              isOpen: bool,
              countries: [getCountryOption(modal.country)],
            });
          }}
          onAddToGroup={(bool) => {
            setAddToGroupModal({
              isOpen: bool,
              countries: [getCountryOption(modal.country)],
            });
          }}
        />
      ) : (
        ''
      )}

      {addToCaseModal.isOpen ? (
        <AddToNarrativeModal
          isOpen={addToCaseModal.isOpen}
          deduplication={false}
          caseId={report?.id}
          eventIds={[story?.id]}
          areAllMessagesSelected={true}
          startDate={startDate}
          endDate={endDate}
          sourceGroups={sourceGroups}
          countries={addToCaseModal.countries}
          onChange={(bool) => {
            setAddToCaseModal({
              isOpen: bool,
              countries: null,
            });
          }}
        />
      ) : (
        ''
      )}
      {addToGroupModal.isOpen ? (
        <AddToSourceGroupModal
          isOpen={addToGroupModal.isOpen}
          deduplication={false}
          areAllMessagesSelected={true}
          startDate={startDate}
          endDate={endDate}
          sourceGroups={sourceGroups}
          countries={addToGroupModal.countries}
          narrative={report}
          eventIds={[story?.id]}
          onChange={(bool) => {
            setAddToGroupModal({
              isOpen: bool,
              countries: null,
            });
          }}
        />
      ) : (
        ''
      )}
    </div>
  );
};